import { clearMomentsPrivateState, setChatId } from '../slices/appSlice';

import { api } from './index';
import {
  momentsAnalyticsSessionClient,
  momentsTokenClient,
} from './momentsApi';

import type { User } from '@/types';

import { cometChatSession } from '@/services/cometChatSession';
import { releaseFullStoryIdentityForLogout } from '@/utils/tracking';

export interface AuthStatusResponse {
  emailVerified: boolean;
  graceExpiresAt: string | null;
  mfaCanSkip: boolean;
  mfaPrompt: boolean;
  mfaRequired: boolean;
  mfaSatisfied: boolean;
  oauthExempt: boolean;

  smsPhoneOnFile?: boolean;
  smsPhoneVerified?: boolean;
  smsMasked?: string | null;
  smsSupportedCountryIsos?: string[];
  totpConfigured?: boolean;
  mfaDefaultMethod?: 'sms' | 'totp' | null;
  b2bMigrationPhase?: string;
  b2bMigrationActive?: boolean;
  b2bLoginEnabled?: boolean;
  b2bCookieWriteEnabled?: boolean;
}

export interface AuthConfigResponse {
  b2bMigrationPhase: string;
  b2bMigrationActive: boolean;
  b2bLoginEnabled: boolean;
}

export interface MfaSmsSetPhoneRequest {
  phone: string;
  countryIso2?: string | null;
}

export interface MfaSmsSetPhoneResponse {
  smsPhoneOnFile: boolean;
  smsPhoneVerified: boolean;
  smsMasked: string | null;
}

export interface MfaSmsSendResponse {
  methodId: string;
  smsMasked?: string | null;
}

export interface MfaSmsAuthenticateRequest {
  methodId?: string;
  otpCode: string;
}

export interface MfaTotpCreateResponse {
  totpId: string;
  qrCode: string;
  secret?: string;
  recoveryCodes?: string[];
}

export interface MfaTotpAuthenticateRequest {
  totpCode: string;
  totpId?: string;
}

export interface StytchSessionHandoffRequest {
  sessionJwt?: string;
  sessionToken?: string;
}

export interface DiscoveryAuthenticateRequest {
  email?: string;
  token: string;
  tokenType: string;
}

export interface DiscoveryAuthenticateResponse {
  type?: string;
  reasonCode?: string;
  authSource?: string;
  email?: string;
  member?: {
    emailAddress?: string;
    email_address?: string;
    memberId?: string;
    member_id?: string;
    name?: {
      firstName?: string;
      first_name?: string;
      lastName?: string;
      last_name?: string;
    };
  };
  session?: {
    expires_at?: string;
  };
  sessionJwt?: string;
  sessionToken?: string;
  token?: string;
}

export interface DiscoveryConfirmRequest {
  token: string;
}

export interface LoginRequest {
  emails: { email: string }[];
  name: {
    first_name: string;
    last_name: string;
  };
  providers: { profile_picture_url?: string }[];
  user_id: string;
}

export type SignInMethod = 'google' | 'microsoft' | 'password' | 'any';

export interface SignInMethodRequest {
  email: string;
}

export interface SignInMethodResponse {
  allowPassword: boolean;
  method: SignInMethod;
}

export const SIGN_IN_METHOD_TIMEOUT_MS = 3000;

// Classification only steers which sign-in options the page shows first, so
// any failure, timeout or unexpected answer shows every option.
export const SIGN_IN_METHOD_FALLBACK: SignInMethodResponse = {
  allowPassword: true,
  method: 'any',
};

const SIGN_IN_METHODS: readonly SignInMethod[] = [
  'google',
  'microsoft',
  'password',
  'any',
];

const parseSignInMethod = (result: {
  data?: unknown;
  error?: unknown;
  meta?: { status?: number };
}): SignInMethodResponse => {
  if (result.error || result.meta?.status !== 200) {
    return SIGN_IN_METHOD_FALLBACK;
  }
  const body = result.data as Partial<SignInMethodResponse> | undefined;
  if (
    !body ||
    !SIGN_IN_METHODS.includes(body.method as SignInMethod) ||
    typeof body.allowPassword !== 'boolean'
  ) {
    return SIGN_IN_METHOD_FALLBACK;
  }
  return { allowPassword: body.allowPassword, method: body.method! };
};

// API returns the application User directly

type LoginUserArgs = LoginRequest & { stytch_token_type?: string };

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAuthStatus: builder.query<AuthStatusResponse, void>({
      providesTags: ['AuthStatus'],
      query: () => ({
        method: 'GET',
        url: '/auth/status',
      }),
    }),

    getAuthConfig: builder.query<AuthConfigResponse, void>({
      query: () => ({
        method: 'GET',
        url: '/auth/config',
      }),
    }),

    getSignInMethod: builder.mutation<
      SignInMethodResponse,
      SignInMethodRequest
    >({
      queryFn: async ({ email }, _queryApi, _extraOptions, baseQuery) => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const timedOut = new Promise<null>((resolve) => {
          timer = setTimeout(() => resolve(null), SIGN_IN_METHOD_TIMEOUT_MS);
        });
        try {
          const result = await Promise.race([
            baseQuery({
              data: { email },
              method: 'POST',
              url: '/auth/sign_in_method',
            }),
            timedOut,
          ]);
          return {
            data: result ? parseSignInMethod(result) : SIGN_IN_METHOD_FALLBACK,
          };
        } catch {
          return { data: SIGN_IN_METHOD_FALLBACK };
        } finally {
          clearTimeout(timer);
        }
      },
    }),

    sendEmailVerification: builder.mutation<void, void>({
      query: () => ({
        method: 'POST',
        url: '/auth/email_verification/send',
      }),
    }),

    handoffStytchSession: builder.mutation<void, StytchSessionHandoffRequest>({
      query: ({ sessionJwt, sessionToken }) => ({
        data: {
          ...(sessionJwt ? { session_jwt: sessionJwt } : {}),
          ...(sessionToken ? { session_token: sessionToken } : {}),
        },
        method: 'POST',
        url: '/auth/stytch/session',
      }),
    }),

    authenticateDiscovery: builder.mutation<
      DiscoveryAuthenticateResponse,
      DiscoveryAuthenticateRequest
    >({
      query: ({ email, token, tokenType }) => ({
        data: {
          ...(email ? { email } : {}),
          token,
          token_type: tokenType,
        },
        method: 'POST',
        url: '/auth/discovery/authenticate',
      }),
    }),

    confirmDiscovery: builder.mutation<
      DiscoveryAuthenticateResponse,
      DiscoveryConfirmRequest
    >({
      query: ({ token }) => ({
        data: { token },
        method: 'POST',
        url: '/auth/discovery/confirm',
      }),
    }),

    loginUser: builder.mutation<User, LoginUserArgs>({
      invalidatesTags: [
        'User',
        'Session',
        'MomentsApiSession',
        'MomentsArtifactSurvey',
        'MomentsCoachMembers',
        'MomentsFeatureFlags',
        'AuthStatus',
        'PulseReport',
      ],
      queryFn: async (
        { emails, name, providers, user_id, stytch_token_type },
        queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        const result = await baseQuery({
          data: {
            avatarUrl: providers[0]?.profile_picture_url ?? '',
            email: emails[0]?.email,
            stytch_token_type,
            sub: user_id,
            ...name, // first_name, last_name
          },
          method: 'POST',
          url: '/login',
        });

        if (result.error) {
          // Keep the existing token cache when login fails; the active session did not change.
          return { error: result.error };
        }

        // Abort old-session requests before resetting private state and admitting
        // the new viewer. Tag invalidation alone can expose retained private data.
        for (const query of queryApi.dispatch(
          api.util.getRunningQueriesThunk(),
        )) {
          query.abort();
        }
        cometChatSession.resume();
        queryApi.dispatch(setChatId(''));
        queryApi.dispatch(clearMomentsPrivateState());
        momentsTokenClient.clear();
        momentsAnalyticsSessionClient.clear();
        queryApi.dispatch(api.util.resetApiState());
        return { data: result.data as User };
      },
    }),

    logoutUser: builder.mutation<void, void>({
      invalidatesTags: [
        'User',
        'Session',
        'MomentsApiSession',
        'MomentsArtifactSurvey',
        'MomentsCoachMembers',
        'MomentsFeatureFlags',
        'AuthStatus',
        'PulseReport',
      ],
      onQueryStarted: (_arg, { dispatch }) => {
        cometChatSession.suspend();
        dispatch(setChatId(''));
        releaseFullStoryIdentityForLogout();
        momentsTokenClient.clear();
        momentsAnalyticsSessionClient.clear();
        // Remove retained private content as soon as sign-out starts. Successful
        // login also resets, covering requests restarted while logout is pending.
        for (const query of dispatch(api.util.getRunningQueriesThunk())) {
          query.abort();
        }
        dispatch(clearMomentsPrivateState());
        dispatch(api.util.resetApiState());
      },
      query: () => ({
        method: 'DELETE',
        url: '/logout',
      }),
    }),

    mfaSmsAuthenticate: builder.mutation<void, MfaSmsAuthenticateRequest>({
      query: ({ methodId, otpCode }) => ({
        data: {
          otp_code: otpCode,
          ...(methodId ? { method_id: methodId } : {}),
        },
        method: 'POST',
        url: '/mfa/sms/authenticate',
      }),
    }),

    mfaSmsSend: builder.mutation<MfaSmsSendResponse, void>({
      query: () => ({
        method: 'POST',
        url: '/mfa/sms/send',
      }),
    }),

    mfaSmsSetPhone: builder.mutation<
      MfaSmsSetPhoneResponse,
      MfaSmsSetPhoneRequest
    >({
      query: (data) => ({
        data,
        method: 'POST',
        url: '/mfa/sms/set_phone',
      }),
    }),

    mfaTotpAuthenticate: builder.mutation<void, MfaTotpAuthenticateRequest>({
      query: ({ totpCode, totpId }) => ({
        data: {
          totp_code: totpCode,
          ...(totpId ? { totp_id: totpId } : {}),
        },
        method: 'POST',
        url: '/mfa/totp/authenticate',
      }),
    }),

    mfaTotpCreate: builder.mutation<
      MfaTotpCreateResponse,
      { reenrollment?: boolean } | void
    >({
      query: (arg) => ({
        method: 'POST',
        url: '/mfa/totp/create',
        data:
          arg && 'reenrollment' in arg
            ? { reenrollment: arg.reenrollment }
            : undefined,
      }),
    }),
  }),
});

export const {
  useGetAuthConfigQuery,
  useGetAuthStatusQuery,
  useLazyGetAuthStatusQuery,
  useGetSignInMethodMutation,
  useSendEmailVerificationMutation,
  useHandoffStytchSessionMutation,
  useAuthenticateDiscoveryMutation,
  useConfirmDiscoveryMutation,
  useMfaSmsSetPhoneMutation,
  useMfaSmsSendMutation,
  useMfaSmsAuthenticateMutation,
  useMfaTotpCreateMutation,
  useMfaTotpAuthenticateMutation,
  useLoginUserMutation,
  useLogoutUserMutation,
} = authApi;

// Backward compatibility wrappers for existing API pattern
export const useLogin = () => {
  const [trigger, result] = useLoginUserMutation();
  return {
    mutate: (
      variables: LoginUserArgs,
      options?: {
        onSuccess?: (data: User) => void;
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      },
    ) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

export const useLogout = () => {
  const [trigger, result] = useLogoutUserMutation();
  return {
    mutate: (options?: {
      onSuccess?: () => void;
      onError?: (error: unknown) => void;
      onSettled?: () => void;
    }) => {
      trigger()
        .unwrap()
        .then(() => {
          options?.onSuccess?.();
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};
